import { describePriceError } from './priceErrors';

// Compare → Merge listings: the run loop's decisions and the modal's labels.
// Groups merge ONE AT A TIME (one Skybox write in flight). A merge is never
// re-sent automatically: a 429 or a 503 "not ready" stops the run (nothing
// more is sent), and an unknown outcome stops it too, because Skybox may have
// merged the group.

export const UNKNOWN_OUTCOME_TEXT = "Skybox didn't confirm — refresh and check before retrying.";
const UNKNOWN_OUTCOME_CODE = 'UNKNOWN_OUTCOME';
const DEFAULT_RETRY_SEC = 30;

// Merge results a group can be run again from (nothing reached Skybox)
const RUNNABLE = new Set(['rate_limited', 'unavailable', 'not_sent']);
// Undo results it can be tried again from
const UNDO_RETRYABLE = new Set(['failed', 'rate_limited', 'unavailable']);
// Undo results where nothing reached the server's write path
const UNDO_NOT_SENT = new Set(['rate_limited', 'unavailable']);
// Results that changed, or may have changed, Skybox
const CONSUMING = new Set(['merged', 'unknown']);
const UNDO_CONSUMING = new Set(['undone', 'unknown']);

const serverMessage = (err) => {
  const msg = err?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
};

const idStrings = (ids) => (Array.isArray(ids) ? ids.filter(id => id != null && id !== '').map(String) : []);
const cleanWarnings = (w) => (Array.isArray(w) ? w.filter(x => typeof x === 'string' && x) : []);
const sourceIdsOf = (g) => idStrings((Array.isArray(g?.sources) ? g.sources : []).map(s => s?.id));
const unknownStop = () => ({ kind: 'unknown', message: UNKNOWN_OUTCOME_TEXT, retryAt: null });

/**
 * Plain reading of a failed merge or undo call.
 * - 429 → rate_limited, with the instant a retry is worth it (body retryAfter,
 *   then Retry-After, else 30 s: Compare's describePriceError).
 * - 503 { retryable: true } → unavailable: nothing was sent (inventory cache
 *   cold / behind, merged listing not synced yet); the server's message.
 * - 502 { code: 'UNKNOWN_OUTCOME' }, no response, an axios timeout, any other
 *   5xx except a 502 carrying our server's { error } → unknown (fail closed:
 *   Skybox may have done it).
 * - Anything else (409 incl. PRICE_CHANGED, 403 incl. LIMIT_EXCEEDED, 502
 *   Skybox refused, 400) → failed, with the server's message.
 * @returns {{ kind: 'failed'|'rate_limited'|'unavailable'|'unknown', message: string, retryAt: number|null }}
 */
export function classifyMergeError(err, now = Date.now()) {
  const status = err?.response?.status;
  const message = serverMessage(err);
  if (status === 429) {
    const { retryAfterSec } = describePriceError(err);
    return {
      kind: 'rate_limited',
      message: message || 'Too many requests — this one was not sent.',
      retryAt: now + (retryAfterSec || DEFAULT_RETRY_SEC) * 1000,
    };
  }
  if (status === 503 && err.response.data?.retryable === true) {
    return { kind: 'unavailable', message: message || 'Not ready yet — nothing was sent. Try again in a moment.', retryAt: null };
  }
  const unknown = !err?.response
    || err?.code === 'ECONNABORTED'
    || err.response.data?.code === UNKNOWN_OUTCOME_CODE
    || (status >= 500 && !(status === 502 && message));
  if (unknown) return unknownStop();
  return {
    kind: 'failed',
    message: message || (status === 409 ? 'No longer valid — refresh the plan.' : err?.message || 'Failed'),
    retryAt: null,
  };
}

/**
 * The ids to send for a group, in the server's order (highest B first: Skybox
 * kept the higher B in 16 prod merges on 2026-10-14, first-id order ignored;
 * the server sends the highest first anyway). Null when the group is not sendable: fewer
 * than 2 sources, a non-integer or duplicate id.
 * @returns {number[]|null}
 */
export function inventoryIdsOf(group) {
  const sources = Array.isArray(group?.sources) ? group.sources : [];
  const ids = sources.map(s => (s?.id == null || s.id === '' ? NaN : Number(s.id)));
  if (ids.length < 2 || !ids.every(id => Number.isSafeInteger(id) && id > 0)) return null;
  return new Set(ids).size === ids.length ? ids : null;
}

/**
 * What one merge request carries besides the event: the ids (server order)
 * and the plan's B as expectedListPrice (the highest of the group; 0 = it
 * stays unpriced; the server refuses with 409 PRICE_CHANGED when it moved).
 * Null when either is unusable (a negative B is junk).
 * @returns {{ inventoryIds: number[], expectedListPrice: number }|null}
 */
export function mergePayloadOf(group) {
  const inventoryIds = inventoryIdsOf(group);
  const raw = group?.listPrice;
  const expectedListPrice = raw == null || raw === '' ? NaN : Number(raw);
  if (!inventoryIds || !Number.isFinite(expectedListPrice) || expectedListPrice < 0) return null;
  return { inventoryIds, expectedListPrice };
}

// A 200 body → the group's merged result. New listing ids are the proof
// Skybox merged; a missing merge id only means our record failed (no Undo).
// Null (outcome unknown) when no new listing id came back.
function mergedResult(data, sourceIds) {
  const m = data?.merge;
  const mergedIds = idStrings(m?.mergedIds);
  if (!mergedIds.length) return null;
  return {
    status: 'merged',
    mergeId: m.id == null || m.id === '' ? null : m.id,
    mergedIds,
    seats: m.seats ?? null,
    quantity: m.quantity ?? null,
    listPrice: m.listPrice ?? null,
    warnings: cleanWarnings(m.warnings),
    sourceIds,
  };
}

/**
 * Merges the groups one after another (never in parallel). A failed group
 * (409, 403 limits, Skybox refused) is marked and the run goes on; a 429, a
 * 503 "not ready" or an unknown outcome stops it, and so does shouldContinue()
 * turning false (checked before every send); the rest are marked not_sent.
 * @param {Array} groups plan groups, in run order
 * @param {(payload: { inventoryIds: number[], expectedListPrice: number }, group: object) => Promise<object>} mergeOne
 *        one POST, resolves to the response body
 * @param {{ onUpdate?: (key: string, result: object) => void, shouldContinue?: () => boolean }} [opts]
 * @returns {Promise<{ results: Object<string, object>, stop: null|{ kind, message, retryAt } }>}
 */
export async function runMergeGroups(groups, mergeOne, { onUpdate, shouldContinue } = {}) {
  const list = Array.isArray(groups) ? groups : [];
  const results = {};
  const put = (key, result) => { results[key] = result; onUpdate?.(key, result); };
  const stopFrom = (i, stop) => {
    for (const rest of list.slice(i)) put(rest.key, { status: 'not_sent', sourceIds: sourceIdsOf(rest) });
    return { results, stop };
  };
  for (let i = 0; i < list.length; i++) {
    if (shouldContinue && !shouldContinue()) {
      return stopFrom(i, { kind: 'cancelled', message: 'Stopped: the window was closed.', retryAt: null });
    }
    const g = list[i];
    const sourceIds = sourceIdsOf(g);
    const payload = mergePayloadOf(g);
    if (!payload) {
      put(g.key, { status: 'failed', error: 'This group has bad listing ids or no B — refresh the plan.', sourceIds });
      continue;
    }
    put(g.key, { status: 'merging', sourceIds });
    let stop = null;
    try {
      const merged = mergedResult(await mergeOne(payload, g), sourceIds);
      if (merged) put(g.key, merged);
      else stop = unknownStop();
    } catch (err) {
      const info = classifyMergeError(err);
      if (info.kind === 'failed') put(g.key, { status: 'failed', error: info.message, sourceIds });
      else stop = info;
    }
    if (stop) {
      put(g.key, { status: stop.kind, error: stop.message, sourceIds });
      return stopFrom(i + 1, stop);
    }
  }
  return { results, stop: null };
}

/** A group can be (re)merged when nothing of it reached Skybox yet. */
export function isRunnable(result) {
  return !result || RUNNABLE.has(result.status);
}

/** An undo can be tried unless one is in flight, it landed, or its outcome is unknown. */
export function canUndo(undo) {
  return !undo || UNDO_RETRYABLE.has(undo.status);
}

/** A 200 undo body → { status: 'undone', restoredIds (Skybox's new ids), mergedIds, warnings }. */
export function undoneResult(data, mergedIds) {
  return {
    status: 'undone',
    restoredIds: idStrings(data?.restoredIds),
    mergedIds: idStrings(mergedIds),
    warnings: cleanWarnings(data?.warnings),
  };
}

/**
 * What the modal session did, for the page on close: changed = anything
 * reached the server (the plan and pricing are re-read); consumedIds = listing
 * ids that are gone, or may be (merged / unknown sources, undone merged ids).
 * @returns {{ changed: boolean, consumedIds: string[] }}
 */
export function sessionChanges(results, undos) {
  const consumed = new Set();
  let changed = false;
  for (const r of Object.values(results || {})) {
    if (!r || isRunnable(r)) continue;
    changed = true;
    if (CONSUMING.has(r.status)) for (const id of r.sourceIds || []) consumed.add(String(id));
  }
  for (const u of Object.values(undos || {})) {
    if (!u || UNDO_NOT_SENT.has(u.status)) continue;
    changed = true;
    if (UNDO_CONSUMING.has(u.status)) for (const id of u.mergedIds || []) consumed.add(String(id));
  }
  return { changed, consumedIds: [...consumed] };
}

/** The plan body's three lists, always arrays; entries without a key / id are dropped. */
export function planParts(data) {
  const arr = (v) => (Array.isArray(v) ? v : []);
  return {
    groups: arr(data?.groups).filter(g => g && g.key != null),
    blocked: arr(data?.blocked).filter(Boolean),
    recent: arr(data?.recent).filter(r => r && r.id != null),
  };
}

/**
 * Which plan groups can run and are ticked. An unknown outcome this session
 * locks the run (Skybox may have merged a group): nothing is selectable until
 * the plan is re-read.
 * @returns {{ runnable: Array, selected: Array, allChecked: boolean, locked: boolean }}
 */
export function runSelection(groups, results, unchecked) {
  const list = Array.isArray(groups) ? groups : [];
  const res = results || {};
  const off = unchecked instanceof Set ? unchecked : new Set();
  const locked = Object.values(res).some(r => r?.status === 'unknown');
  const runnable = locked ? [] : list.filter(g => isRunnable(res[g.key]));
  const selected = runnable.filter(g => !off.has(g.key));
  return { runnable, selected, allChecked: runnable.length > 0 && selected.length === runnable.length, locked };
}

/**
 * Rows to show: the plan's groups, then groups run this session that a
 * re-read plan no longer has, when their result is final (merged, failed,
 * unknown) — a result never vanishes with its group.
 * @param {Array} planGroups current plan groups
 * @param {Object<string, object>} ran group key → the group as it was sent
 * @returns {Array<{ group: object, inPlan: boolean }>}
 */
export function displayGroups(planGroups, ran, results) {
  const list = Array.isArray(planGroups) ? planGroups : [];
  const res = results || {};
  const inPlan = new Set(list.map(g => g.key));
  const left = Object.values(ran || {})
    .filter(g => g && !inPlan.has(g.key) && res[g.key] && !isRunnable(res[g.key]));
  return [...list.map(group => ({ group, inPlan: true })), ...left.map(group => ({ group, inPlan: false }))];
}

/** A failed plan read in words (modal and button tooltip). */
export function planErrorText(err) {
  return serverMessage(err)
    || (err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'network error');
}

/**
 * Compare's "Merge listings (N)" button from the plan query. Stays enabled
 * with nothing to merge while a recent merge can still be undone, and after a
 * failed read ("(!)") so the modal's error and Retry are reachable.
 * @returns {{ count: number, disabled: boolean, label: string, title: string }}
 */
export function mergeButtonState({ isLoading, isError, error, data } = {}) {
  const { groups, recent } = planParts(data);
  const count = groups.length;
  const label = `Merge listings (${count})`;
  if (isLoading) return { count, disabled: true, label, title: 'Checking which listings can merge…' };
  if (isError) {
    return { count, disabled: false, label: 'Merge listings (!)', title: `Merge plan unavailable: ${planErrorText(error)}. Open to retry.` };
  }
  if (!count && !recent.length) return { count, disabled: true, label, title: 'No listings on this event can be merged.' };
  if (!count) {
    return { count, disabled: false, label, title: `Nothing to merge now. Open to undo ${recent.length} recent merge${recent.length === 1 ? '' : 's'}.` };
  }
  return { count, disabled: false, label, title: 'Merge side-by-side listings (same section and row) into one Skybox listing each.' };
}

/** "$121" / "$121.50"; "-" for null, blank or junk. */
export function money(v) {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

// A B of exactly 0 (a number or "0"): the listing is unpriced. Null / blank stay unknown ("-").
const isUnpriced = (v) => v != null && v !== '' && Number(v) === 0;

/** "Sec 304 · Row R · 9-12 (4) · keeps $121" (the highest B), or "· stays unpriced" when it is 0 */
export function groupLabel(g) {
  const kept = isUnpriced(g?.listPrice) ? 'stays unpriced' : `keeps ${money(g?.listPrice)}`;
  return `Sec ${g?.section ?? '-'} · Row ${g?.row ?? '-'} · ${g?.seats ?? '-'} (${g?.quantity ?? '?'}) · ${kept}`;
}

/** "1-4 (4) $130.25 + 5-8 (4) $121", in send order (highest first); a $0 source reads "unpriced" */
export function sourcesLine(g) {
  return (Array.isArray(g?.sources) ? g.sources : [])
    .map(s => `${s?.seats ?? '-'} (${s?.quantity ?? '?'}) ${isUnpriced(s?.listPrice) ? 'unpriced' : money(s?.listPrice)}`)
    .join(' + ');
}

/** "Sec 304 · Row R · 1-4 + 5-8 — tags differ" */
export function blockedLabel(b) {
  return `Sec ${b?.section ?? '-'} · Row ${b?.row ?? '-'} · ${b?.seats ?? '-'} — ${b?.reason || 'not mergeable'}`;
}

/** Tags as an array of names, from an array or a space-separated string. */
export function tagList(tags) {
  if (Array.isArray(tags)) return tags.filter(t => t != null).map(t => String(t).trim()).filter(Boolean);
  if (typeof tags === 'string') return tags.split(/\s+/).filter(Boolean);
  return [];
}
